/**
 * Startup Insights Screen
 *
 * AI-powered coaching and insights for startup founders.
 * Helps improve investment readiness through actionable advice.
 */

import React, { useMemo, useCallback, useState, useEffect } from 'react';
import { View, Text, Pressable } from 'react-native';
import { useRouter } from 'expo-router';
import { AlertCircle } from 'lucide-react-native';
import Animated, { FadeIn } from 'react-native-reanimated';
import { useThemeColors } from '@/lib/useThemeColors';
import { AppLayout } from '@/components/AppLayout';
import { ScreenFrame } from '@/components/ScreenFrame';
import { InsightsContainer } from '@/components/startup-insights/InsightsContainer';
import AnimatedPressable from '@/components/AnimatedPressable';
import {
  profileToInsightsContext,
  addScoresToInsightsContext,
  scoreCriteriaToContextDimensions,
  type InsightsActionItem,
} from '@/lib/startup-insights';
import { useFlagRouteAccess } from '@/lib/feature-flags';
import { useProfileStore } from '@/lib/state/profile-store';
import { useScoreStore } from '@/lib/state/score-store';

/**
 * Calculate overall score from score criteria
 */
function calculateOverallScore(
  criteria: Array<{ score: number; maxScore: number }>
): number {
  if (criteria.length === 0) return 0;
  const totalScore = criteria.reduce((sum, c) => sum + c.score, 0);
  const maxScore = criteria.reduce((sum, c) => sum + c.maxScore, 0);
  return Math.round((totalScore / maxScore) * 100);
}

/**
 * Determine readiness level from score
 */
function determineReadinessLevel(score: number): string {
  if (score >= 81) return 'Excellent';
  if (score >= 66) return 'Strong';
  if (score >= 46) return 'Moderate';
  if (score >= 26) return 'Weak';
  return 'Critical';
}

export default function InsightsScreen() {
  const colors = useThemeColors();
  const router = useRouter();

  // `ai_coach` is seeded OFF globally. Both FAB launchers gate themselves and the web
  // sidebar omits the advisor entirely, but the route is registered `href: null` — hidden
  // from the tab bar, still reachable by router.push and by deep link — so without this it
  // was open to anyone who knew the path.
  //
  // useFlagRouteAccess, NOT useFlag: a redirect must not fire on a default. See
  // src/lib/feature-flags/route-access.ts.
  const advisorAccess = useFlagRouteAccess('ai_coach');

  // Get startup data from store
  const startupProfile = useProfileStore((s) => s.startupProfile);
  const scoreCriteria = useScoreStore((s) => s.scoreCriteria);

  // Track action items locally (optional: could persist to store)
  const [savedActionItems, setSavedActionItems] = useState<InsightsActionItem[]>([]);

  // Build context from profile and scores
  const startupContext = useMemo(() => {
    if (!startupProfile) return null;

    // Convert profile to insights context
    let context = profileToInsightsContext(startupProfile);

    // Add score data if available
    if (scoreCriteria.length > 0) {
      const finalScore = calculateOverallScore(scoreCriteria);
      const readinessLevel = determineReadinessLevel(finalScore);
      const dimensions = scoreCriteriaToContextDimensions(scoreCriteria);

      context = addScoresToInsightsContext(context, {
        finalScore,
        readinessLevel,
        dimensions,
        topStrengths: dimensions
          .filter((d) => d.score >= 70)
          .map((d) => d.name)
          .slice(0, 3),
        topRisks: dimensions
          .filter((d) => d.score < 50)
          .map((d) => d.name)
          .slice(0, 3),
        recommendations: dimensions
          .filter((d) => d.weakness)
          .map((d) => d.weakness as string)
          .slice(0, 5),
      });
    }

    return context;
  }, [startupProfile, scoreCriteria]);

  // Handle action items changes
  const handleActionItemsChange = useCallback((items: InsightsActionItem[]) => {
    setSavedActionItems((prev) => {
      // Merge new items, avoiding duplicates
      const existing = new Set(prev.map((a) => a.action));
      const newItems = items.filter((item) => !existing.has(item.action));
      return [...prev, ...newItems];
    });
  }, []);

  // Leaving is a side effect, so it belongs in an effect rather than in render. Declared
  // below every other hook and above the first early return, so hook order is stable
  // across the loading -> resolved transition.
  useEffect(() => {
    if (advisorAccess === 'redirect') {
      // The parent surface, which is also this screen's back fallback — a blocked deep
      // link lands where the feature lives rather than at the root.
      router.replace('/(startup)/recommendations');
    }
  }, [advisorAccess, router]);

  // Nothing renders until the flag is known. 'loading' is a real third answer here: showing
  // the advisor and retracting it would flash a paused product, and redirecting would throw
  // out a user who actually holds the flag.
  if (advisorAccess !== 'allowed') {
    return <View style={{ flex: 1 }} />;
  }

  // No profile state
  if (!startupProfile) {
    return (
      <AppLayout title="AI Advisor">
        <View className="flex-1" style={{ backgroundColor: colors.surface.primary }}>
          <ScreenFrame
            title="AI Advisor"
            back={{ fallback: '/(startup)/recommendations', label: 'Recommendations' }}
            backgroundColor={colors.surface.primary}
          >
            <View className="flex-1 items-center justify-center px-8">
              <View
                className="w-20 h-20 rounded-full items-center justify-center mb-4"
                style={{ backgroundColor: colors.amber.light }}
              >
                <AlertCircle size={40} color={colors.warning} />
              </View>
              <Text
                style={{ color: colors.text.primary }}
                className="text-xl font-bold text-center mb-2"
              >
                Complete Your Profile
              </Text>
              <Text
                style={{ color: colors.text.tertiary }}
                className="text-sm text-center leading-relaxed mb-6"
              >
                To receive personalized AI coaching, please complete your
                startup profile first. This helps us understand your business
                and provide relevant insights.
              </Text>
              <AnimatedPressable
                onPress={() => router.push('/(startup)/company')}
                className="px-6 py-3 rounded-xl"
                style={{ backgroundColor: colors.primary[600] }}
              >
                <Text style={{ color: colors.text.inverse }} className="font-semibold">
                  Complete Profile
                </Text>
              </AnimatedPressable>
            </View>
          </ScreenFrame>
        </View>
      </AppLayout>
    );
  }

  return (
    <AppLayout title="AI Advisor">
      <View className="flex-1" style={{ backgroundColor: colors.surface.primary }}>
        {/* The back control here used to render only when history existed, so
           a deep link into the advisor had no visible way out at all. The frame
           makes the parent explicit instead. */}
        <ScreenFrame
          title="AI Advisor"
          eyebrow={startupProfile.name}
          back={{ fallback: '/(startup)/recommendations', label: 'Recommendations' }}
          backgroundColor={colors.surface.primary}
        >

          {/* Score Context Banner */}
          {scoreCriteria.length > 0 && (
            <Animated.View
              entering={FadeIn.delay(200).duration(400)}
              className="mx-5 mt-4 p-4 rounded-xl"
              style={{ backgroundColor: colors.surface.secondary }}
            >
              <View className="flex-row items-center justify-between">
                <View>
                  <Text
                    style={{ color: colors.text.tertiary }}
                    className="text-xs font-medium"
                  >
                    CURRENT SCORE
                  </Text>
                  <Text
                    style={{ color: colors.text.primary }}
                    className="text-2xl font-bold"
                  >
                    {calculateOverallScore(scoreCriteria)}/100
                  </Text>
                </View>
                <View className="items-end">
                  <Text
                    style={{ color: colors.text.tertiary }}
                    className="text-xs"
                  >
                    {determineReadinessLevel(calculateOverallScore(scoreCriteria))}
                  </Text>
                  {savedActionItems.length > 0 && (
                    <Text
                      style={{ color: colors.primary[600] }}
                      className="text-xs font-medium mt-1"
                    >
                      {savedActionItems.length} action items saved
                    </Text>
                  )}
                </View>
              </View>
            </Animated.View>
          )}

          {/* Insights Container */}
          <View className="flex-1 mt-4">
            {startupContext && (
              // presentation="page": this IS the screen, so the panel draws no
              // close button and no minimise control — the frame above owns
              // leaving, and a full page has no window to shrink.
              <InsightsContainer
                startupContext={startupContext}
                startupId={startupProfile.id}
                presentation="page"
                onActionItemsChange={handleActionItemsChange}
              />
            )}
          </View>
        </ScreenFrame>
      </View>
    </AppLayout>
  );
}
